import ScreenLayout from 'components/common/ScreenLayout';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment-timezone';
import styled, {css} from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';
import DefaultInput from 'components/common/DefaultInput';
import DefaultImageView from 'components/common/DefaultImageView';
import ContainerHeader from 'components/common/ContainerHeader';
import Pagination from 'components/common/Pagination';
import TableSortButton, {SortOrder} from 'components/common/TableSortButton';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {userInfoState} from 'utils/Atoms';
import {
  IContractorStat,
  ContractorSortBy,
  IContractorStatSite,
  IContractorStatisticsRaw,
  IStatPeriod,
  ISitePartnerCompany,
  aggregateContractorStatistics,
  fetchContractorStatisticsRaw,
  getPartnerDedupeKey,
  getSiteAttendeeCounts,
  getSiteAttendanceLabel,
  getSitePartnerCompanies,
  isCreatedInPeriod,
  sortContractorStatistics,
  SiteAttendanceState,
} from 'function/statistics';
import {getSiteStatusKey, siteStatusMeta} from 'utils/siteStatus';

/** 값이 비어있을 때의 표시 문자열 */
const EMPTY_NAME = '미지정';

/** 신규 항목 드릴다운 모달 종류 */
type NewItemModal = 'contractors' | 'sites' | 'partners' | 'operating';

/** 한국 표준시(KST) 고정 타임존 */
const KST = 'Asia/Seoul';

/**
 * createdAt(ISO)을 KST 기준 YYYY-MM-DD로 표시합니다.
 * 기간 필터(monthToPeriod)도 KST 기준이라, 브라우저 타임존과 무관하게
 * 필터·표시가 항상 한국시간으로 일관됩니다.
 */
const formatDate = (createdAt?: string | null): string => {
  if (!createdAt) {
    return '-';
  }
  const m = moment.tz(createdAt, KST);
  return m.isValid() ? m.format('YYYY-MM-DD') : '-';
};

/** 한 페이지에 표시할 시공사 수 */
const ROWS_PER_PAGE = 50;

/** 기간 필터 모드 */
type PeriodMode = 'all' | 'month';

/** 'YYYY-MM' 형식의 KST 기준 현재 월 문자열을 반환합니다. */
const getCurrentMonth = (): string => moment.tz(KST).format('YYYY-MM');

/**
 * 'YYYY-MM' 월 문자열을 KST 기준 월 구간(epoch ms, IStatPeriod)으로 변환합니다.
 * epoch 비교이므로 브라우저 타임존과 무관하게 한국시간 한 달 범위가 됩니다.
 * 잘못된 값이면 null(전체)을 반환합니다.
 */
const monthToPeriod = (month: string): IStatPeriod | null => {
  const start = moment.tz(month, 'YYYY-MM', KST).startOf('month');
  if (!start.isValid()) {
    return null;
  }
  return {
    start: start.valueOf(),
    end: start.clone().add(1, 'month').valueOf(),
  };
};

/** 현장별 협력사 지연 조회 상태 */
type SitePartnerState = 'loading' | ISitePartnerCompany[];

/**
 * 상태 배지 컴포넌트
 */
const StatusBadge = ({statusKey}: {statusKey: keyof typeof siteStatusMeta}) => {
  const meta = siteStatusMeta[statusKey];
  return <Badge style={{backgroundColor: meta.color}}>{meta.label}</Badge>;
};

/**
 * 숫자 요약 카드 컴포넌트
 */
const SummaryCard = ({
  label,
  value,
  accent,
  onClick,
}: {
  label: string;
  value: number;
  accent?: string;
  onClick?: () => void;
}) => (
  <Card $clickable={onClick ? 1 : 0} onClick={onClick}>
    <CardValue style={accent ? {color: accent} : undefined}>
      {value.toLocaleString()}
    </CardValue>
    <CardLabel>
      {label}
      {onClick && <CardHint> 목록 ›</CardHint>}
    </CardLabel>
  </Card>
);

/**
 * 시공사 통계 페이지
 * 전체 시공사/현장/협력사 통계 요약과 시공사별 상세를 표시합니다.
 */
const ContractorStatistics = () => {
  const navigate = useNavigate();
  const userInfo = useRecoilValue(userInfoState);

  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});
  const [raw, setRaw] = useState<IContractorStatisticsRaw | null>(null);
  const [keyword, setKeyword] = useState('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('all');
  const [month, setMonth] = useState<string>(getCurrentMonth());
  const [sortBy, setSortBy] = useState<ContractorSortBy>('contractorName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newItemModal, setNewItemModal] = useState<NewItemModal | null>(null);
  const [sitePartners, setSitePartners] = useState<{
    [siteId: string]: SitePartnerState;
  }>({});
  /** 현장별 기간 내 출근 인원 (월별 모드 전용) */
  const [siteAttendance, setSiteAttendance] = useState<{
    [siteId: string]: SiteAttendanceState;
  }>({});
  const sitePartnerRequestsRef = useRef<Set<string>>(new Set());

  const loadStatistics = useCallback(async () => {
    setIsLoading(true);
    const result = await fetchContractorStatisticsRaw({
      userInfo,
      setErrorModal,
    });
    if (result) {
      setRaw(result);
    }
    setIsLoading(false);
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, [navigate, userInfo?.accessToken]);

  useEffect(() => {
    if (userInfo?.accessToken) {
      loadStatistics();
    }
  }, [loadStatistics, userInfo?.accessToken]);

  /** 기간 구간 (월별 선택 시에만 적용, 전체면 null) */
  const period = useMemo<IStatPeriod | null>(
    () => (periodMode === 'month' ? monthToPeriod(month) : null),
    [periodMode, month],
  );

  /** 원본 + 기간으로 통계 재집계 */
  const stats = useMemo(
    () => (raw ? aggregateContractorStatistics(raw, period) : null),
    [raw, period],
  );

  /** 해당 기간 신규 시공사 목록 (최신순) */
  const newContractorList = useMemo(() => {
    if (!raw) {
      return [];
    }
    return raw.contractors
      .filter(c => isCreatedInPeriod(c.createdAt, period))
      .sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime(),
      );
  }, [raw, period]);

  /** 해당 기간 신규 현장 목록 (최신순) */
  const newSiteList = useMemo(() => {
    if (!raw) {
      return [];
    }
    return raw.sites
      .filter(s => isCreatedInPeriod(s.createdAt, period))
      .sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime(),
      );
  }, [raw, period]);

  /** 해당 기간 신규 협력사 목록 (uuid 중복 제거, 최신순) */
  const newPartnerList = useMemo(() => {
    if (!raw) {
      return [];
    }
    const seen = new Set<string>();
    return raw.partners
      .filter(p => isCreatedInPeriod(p?.createdAt, period))
      .filter(p => {
        const id = getPartnerDedupeKey(p);
        if (!id || seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
      .sort(
        (a: any, b: any) =>
          new Date(b?.createdAt ?? 0).getTime() -
          new Date(a?.createdAt ?? 0).getTime(),
      );
  }, [raw, period]);

  /**
   * 해당 월에 '운영 중이던' 현장 목록.
   * 정의: 현장 운영기간(constructionStartDate~EndDate)이 선택 월과 겹치면 그 달 운영 중으로 본다.
   * (생성일 기준이 아니라 운영기간 기준이라, 과거에 만들어졌어도 그 달에 운영 중이면 포함)
   */
  const operatingSiteList = useMemo(() => {
    if (!raw || !period) {
      return [];
    }
    return raw.sites
      .filter(s => {
        const start = s.constructionStartDate
          ? new Date(s.constructionStartDate).getTime()
          : NaN;
        const end = s.constructionEndDate
          ? new Date(s.constructionEndDate).getTime()
          : NaN;
        if (Number.isNaN(start) || Number.isNaN(end)) {
          return false;
        }
        // [start, end] 운영 윈도우가 [period.start, period.end) 와 겹치는가
        return start < period.end && end >= period.start;
      })
      .sort(
        (a, b) =>
          new Date(a.constructionStartDate ?? 0).getTime() -
          new Date(b.constructionStartDate ?? 0).getTime(),
      );
  }, [raw, period]);

  /** 키워드로 시공사 필터링 */
  const filteredContractors = useMemo(() => {
    if (!stats) {
      return [];
    }
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) {
      return stats.contractors;
    }
    return stats.contractors.filter(c =>
      (c.contractorName || EMPTY_NAME).toLowerCase().includes(trimmed),
    );
  }, [stats, keyword]);

  const sortedContractors = useMemo(
    () => sortContractorStatistics(filteredContractors, sortBy, sortOrder),
    [filteredContractors, sortBy, sortOrder],
  );

  /** 페이지네이션 (시공사 50개 / 페이지) */
  const totalPage = Math.max(
    1,
    Math.ceil(sortedContractors.length / ROWS_PER_PAGE),
  );
  const pagedContractors = useMemo(
    () =>
      sortedContractors.slice(
        (currentPage - 1) * ROWS_PER_PAGE,
        currentPage * ROWS_PER_PAGE,
      ),
    [sortedContractors, currentPage],
  );

  /** 검색/기간/정렬 변경 시 1페이지로 초기화 */
  useEffect(() => {
    setCurrentPage(1);
  }, [keyword, periodMode, month, sortBy, sortOrder]);

  /**
   * 월별 모드에서 펼친 시공사의 현장별 출근 인원을 조회합니다.
   * (전체 기간은 제외 — period가 있을 때만) 기간/펼침 변경 시 자동 갱신됩니다.
   */
  useEffect(() => {
    if (periodMode !== 'month' || !period || !expandedId || !stats) {
      return;
    }
    const contractor = stats.contractors.find(
      c => (c.contractorId || c.contractorName || 'unassigned') === expandedId,
    );
    if (!contractor) {
      return;
    }
    // 신규 + 운영 현장 합집합 (siteId 중복 제거)
    const siteIds = Array.from(
      new Set(
        [...contractor.sites, ...contractor.operatingSites].map(s => s.siteId),
      ),
    );
    if (siteIds.length === 0) {
      return;
    }
    setSiteAttendance(prev => {
      const next = {...prev};
      siteIds.forEach(id => (next[id] = 'loading'));
      return next;
    });
    let cancelled = false;
    getSiteAttendeeCounts({
      userInfo,
      setErrorModal,
      tongPlaceUUIDList: siteIds,
      period,
    }).then(result => {
      if (cancelled) {
        return;
      }
      setSiteAttendance(prev => {
        const next = {...prev};
        siteIds.forEach(
          id => (next[id] = result === false ? 'error' : result[id] ?? 0),
        );
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [expandedId, period, periodMode, stats, userInfo, setErrorModal]);

  /** 시공사 상세 펼침/접힘 토글 + 펼칠 때 현장별 협력사 지연 조회 */
  const toggleExpand = (contractor: IContractorStat) => {
    const id =
      contractor.contractorId || contractor.contractorName || 'unassigned';
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    loadSitePartners(contractor);
  };

  /** 시공사의 (신규 + 운영) 현장 합집합 — siteId 기준 중복 제거 */
  const getContractorSiteList = (
    contractor: IContractorStat,
  ): IContractorStatSite[] => {
    const map = new Map<string, IContractorStatSite>();
    [...contractor.sites, ...contractor.operatingSites].forEach(s => {
      if (!map.has(s.siteId)) {
        map.set(s.siteId, s);
      }
    });
    return Array.from(map.values());
  };

  /** 펼친 시공사의 현장별 협력사를 (아직 조회 안 한 현장만) 병렬 조회 */
  const loadSitePartners = (contractor: IContractorStat) => {
    getContractorSiteList(contractor).forEach(site => {
      const siteId = site.siteId;
      if (
        sitePartners[siteId] !== undefined ||
        sitePartnerRequestsRef.current.has(siteId)
      ) {
        return;
      }
      sitePartnerRequestsRef.current.add(siteId);
      setSitePartners(prev =>
        prev[siteId] !== undefined ? prev : {...prev, [siteId]: 'loading'},
      );
      getSitePartnerCompanies({
        userInfo,
        setErrorModal,
        tongPlaceUUID: siteId,
      }).then(result => {
        setSitePartners(prev => ({
          ...prev,
          [siteId]: result || [],
        }));
      });
    });
  };

  const renderRowId = (contractor: IContractorStat) =>
    contractor.contractorId || contractor.contractorName || 'unassigned';

  const changeSort = (nextSortBy: ContractorSortBy) => {
    setSortOrder(sortBy === nextSortBy && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(nextSortBy);
  };

  const sortHeader = (
    label: string,
    key: ContractorSortBy,
    style?: React.CSSProperties,
  ) => (
    <HCell style={style}>
      <TableSortButton
        label={label}
        active={sortBy === key}
        order={sortOrder}
        onClick={() => changeSort(key)}
      />
    </HCell>
  );

  /** 현장 1건 행 렌더 (신규/운영 목록 공통) */
  const renderSiteRow = (site: IContractorStatSite) => {
    const sp = sitePartners[site.siteId];
    const att = siteAttendance[site.siteId];
    const attendanceLabel = getSiteAttendanceLabel(att);
    return (
      <SiteRow key={site.siteId}>
        <SiteName>{site.siteName || EMPTY_NAME}</SiteName>
        <StatusBadge statusKey={site.statusKey} />
        {periodMode === 'month' && (
          <AttendanceTag $on={typeof att === 'number' && att > 0 ? 1 : 0}>
            {attendanceLabel}
          </AttendanceTag>
        )}
        <SitePartners>
          {sp === undefined ? null : sp === 'loading' ? (
            <MutedText>협력사 불러오는 중…</MutedText>
          ) : sp.length === 0 ? (
            <MutedText>협력사 없음</MutedText>
          ) : (
            <ChipWrap>
              {sp.map(pc => (
                <Chip key={`${site.siteId}-${pc.partnerCompanyId}`}>
                  {pc.partnerCompanyName || EMPTY_NAME}
                </Chip>
              ))}
            </ChipWrap>
          )}
        </SitePartners>
        <SiteMeta>
          생성일 {formatDate(site.createdAt)} · 운영기간{' '}
          {formatDate(site.constructionStartDate)} ~{' '}
          {formatDate(site.constructionEndDate)}
        </SiteMeta>
      </SiteRow>
    );
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <ContainerHeader
          mainTitle="contractorManagement"
          subTitle="contractorStatistics"
        />

        <Inner>
          <DefaultText
            text="시공사 통계"
            textStyle={{
              fontSize: minmax(25),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />

          {/* 상단 요약 카드 (월별 모드에서는 일부 카드가 '해당 월 신규' 지표로 전환) */}
          <CardGrid>
            <SummaryCard
              label={
                periodMode === 'month'
                  ? '해당 월 신규 시공사 수'
                  : '전체 시공사 수'
              }
              value={
                (periodMode === 'month'
                  ? stats?.newContractorCount
                  : stats?.totalContractorCount) ?? 0
              }
              accent={periodMode === 'month' ? colors.primary2 : undefined}
              onClick={
                periodMode === 'month'
                  ? () => setNewItemModal('contractors')
                  : undefined
              }
            />
            <SummaryCard
              label="전체 현장 수"
              value={stats?.totalSiteCount ?? 0}
            />
            {periodMode === 'month' && (
              <SummaryCard
                label="해당 월 운영 현장 수"
                value={operatingSiteList.length}
                accent={siteStatusMeta.operating.color}
                onClick={() => setNewItemModal('operating')}
              />
            )}
            {/* 현재상태 기반 분포는 '전체' 모드에서만 표시 (월별 모드에선 혼동 방지로 숨김) */}
            {periodMode === 'all' && (
              <>
                <SummaryCard
                  label={siteStatusMeta.operating.label}
                  value={stats?.siteStatusCounts.operating ?? 0}
                  accent={siteStatusMeta.operating.color}
                />
                <SummaryCard
                  label={siteStatusMeta.paused.label}
                  value={stats?.siteStatusCounts.paused ?? 0}
                  accent={siteStatusMeta.paused.color}
                />
                <SummaryCard
                  label={siteStatusMeta.pendingApproval.label}
                  value={stats?.siteStatusCounts.pendingApproval ?? 0}
                  accent={siteStatusMeta.pendingApproval.color}
                />
                <SummaryCard
                  label={siteStatusMeta.closed.label}
                  value={stats?.siteStatusCounts.closed ?? 0}
                  accent={siteStatusMeta.closed.color}
                />
              </>
            )}
            <SummaryCard
              label={
                periodMode === 'month'
                  ? '해당 월 신규 현장 수'
                  : siteStatusMeta.notStarted.label
              }
              value={
                (periodMode === 'month'
                  ? stats?.periodSiteCount
                  : stats?.siteStatusCounts.notStarted) ?? 0
              }
              accent={
                periodMode === 'month'
                  ? colors.primary2
                  : siteStatusMeta.notStarted.color
              }
              onClick={
                periodMode === 'month'
                  ? () => setNewItemModal('sites')
                  : undefined
              }
            />
            <SummaryCard
              label={
                periodMode === 'month'
                  ? '해당 월 신규 협력사 수'
                  : '전체 협력사 수'
              }
              value={
                (periodMode === 'month'
                  ? stats?.newPartnerCompanyCount
                  : stats?.totalPartnerCompanyCount) ?? 0
              }
              accent={colors.primary}
              onClick={
                periodMode === 'month'
                  ? () => setNewItemModal('partners')
                  : undefined
              }
            />
          </CardGrid>

          {/* 필터: 기간(전체/월별) + 검색 */}
          <FilterBar>
            <PeriodWrap>
              <PeriodLabel>기간</PeriodLabel>
              <PeriodButton
                type="button"
                $active={periodMode === 'all' ? 1 : 0}
                onClick={() => setPeriodMode('all')}>
                전체
              </PeriodButton>
              <PeriodButton
                type="button"
                $active={periodMode === 'month' ? 1 : 0}
                onClick={() => setPeriodMode('month')}>
                월별
              </PeriodButton>
              {periodMode === 'month' && (
                <MonthInput
                  type="month"
                  value={month}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setMonth(e.target.value)
                  }
                />
              )}
            </PeriodWrap>

            <SearchArea>
              <InputWrap>
                <DefaultInput
                  value={keyword}
                  setValue={(e: any) => setKeyword(e)}
                  placeholderText="searchPlaceholder"
                  wrapperStyle={{height: '100%'}}
                />
                <DefaultImageView
                  source="images/common/search.png"
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    cursor: 'pointer',
                  }}
                />
              </InputWrap>
            </SearchArea>
          </FilterBar>

          {/* 결과 건수 안내 */}
          {stats && (
            <ResultMeta>
              {periodMode === 'month'
                ? `${month} 기준 현장 통계`
                : '전체 기간 기준'}{' '}
              · 시공사 {filteredContractors.length.toLocaleString()}곳
            </ResultMeta>
          )}

          {/* 시공사별 통계 테이블 */}
          <ResultArea>
            <ResultHeader>
              {sortHeader('시공사명', 'contractorName', {flex: 2})}
              {sortHeader(
                periodMode === 'month' ? '신규 생성 현장' : '생성 현장 수',
                'siteCount',
              )}
              {periodMode === 'month' &&
                sortHeader('해당 월 운영중', 'operatingSiteCount')}
              {periodMode === 'all' && (
                <>
                  {sortHeader(siteStatusMeta.operating.label, 'operating')}
                  {sortHeader(siteStatusMeta.paused.label, 'paused')}
                  {sortHeader(
                    siteStatusMeta.pendingApproval.label,
                    'pendingApproval',
                  )}
                  {sortHeader(siteStatusMeta.closed.label, 'closed')}
                  {sortHeader('연결 협력사 수', 'partnerCompanyCount')}
                </>
              )}
              <HCell style={{flex: 0.8}}>상세</HCell>
            </ResultHeader>

            {!stats ? null : filteredContractors.length === 0 ? (
              <EmptyMsg>데이터 없음</EmptyMsg>
            ) : (
              pagedContractors.map(contractor => {
                const id = renderRowId(contractor);
                const isOpen = expandedId === id;
                return (
                  <React.Fragment key={id}>
                    <ResultItem
                      $open={isOpen ? 1 : 0}
                      onClick={() => toggleExpand(contractor)}>
                      <Cell
                        style={{
                          flex: 2,
                          fontWeight: 500,
                          color: contractor.isUnassigned
                            ? colors.darkGray
                            : colors.black,
                        }}>
                        {contractor.isUnassigned
                          ? '(미지정 시공사)'
                          : contractor.contractorName || EMPTY_NAME}
                      </Cell>
                      <Cell>{contractor.siteCount}</Cell>
                      {periodMode === 'month' && (
                        <Cell style={{color: siteStatusMeta.operating.color}}>
                          {contractor.operatingSiteCount}
                        </Cell>
                      )}
                      {periodMode === 'all' && (
                        <>
                          <Cell>{contractor.siteStatusCounts.operating}</Cell>
                          <Cell>{contractor.siteStatusCounts.paused}</Cell>
                          <Cell>
                            {contractor.siteStatusCounts.pendingApproval}
                          </Cell>
                          <Cell>{contractor.siteStatusCounts.closed}</Cell>
                          <Cell>{contractor.partnerCompanyCount}</Cell>
                        </>
                      )}
                      <Cell style={{flex: 0.8, color: colors.primary2}}>
                        {isOpen ? '접기 ▲' : '상세 ▼'}
                      </Cell>
                    </ResultItem>

                    {isOpen && (
                      <DetailWrap>
                        {/* 시공사 단위 연결 협력사 */}
                        <DetailSection>
                          <DetailTitle>
                            연결 협력사 ({contractor.partnerCompanyCount})
                          </DetailTitle>
                          {contractor.partnerCompanyNames.length === 0 ? (
                            <MutedText>연결된 협력사 없음</MutedText>
                          ) : (
                            <ChipWrap>
                              {contractor.partnerCompanyNames.map((name, i) => (
                                <Chip key={`${id}-pc-${i}`}>
                                  {name || EMPTY_NAME}
                                </Chip>
                              ))}
                            </ChipWrap>
                          )}
                        </DetailSection>

                        {/* 현장 목록 (월별: 신규 생성 + 해당 월 운영중 / 전체: 전체 현장) */}
                        {periodMode === 'month' ? (
                          <>
                            <DetailSection>
                              <DetailTitle>
                                신규 생성 현장 ({contractor.sites.length})
                                <SubNote>· 해당 월에 생성된 현장</SubNote>
                              </DetailTitle>
                              {contractor.sites.length === 0 ? (
                                <MutedText>
                                  해당 월 신규 생성 현장 없음
                                </MutedText>
                              ) : (
                                contractor.sites.map(renderSiteRow)
                              )}
                            </DetailSection>
                            <DetailSection>
                              <DetailTitle>
                                해당 월 운영중 현장 (
                                {contractor.operatingSites.length})
                                <SubNote>· 운영기간 기준</SubNote>
                              </DetailTitle>
                              {contractor.operatingSites.length === 0 ? (
                                <MutedText>해당 월 운영중 현장 없음</MutedText>
                              ) : (
                                contractor.operatingSites.map(renderSiteRow)
                              )}
                            </DetailSection>
                          </>
                        ) : (
                          <DetailSection>
                            <DetailTitle>
                              현장 목록 ({contractor.siteCount})
                              <SubNote>
                                · 현장별 협력사는 등록 근로자 기준입니다
                              </SubNote>
                            </DetailTitle>
                            {contractor.sites.length === 0 ? (
                              <MutedText>생성한 현장 없음</MutedText>
                            ) : (
                              contractor.sites.map(renderSiteRow)
                            )}
                          </DetailSection>
                        )}
                      </DetailWrap>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </ResultArea>

          {filteredContractors.length > ROWS_PER_PAGE && (
            <Pagination
              totalPage={totalPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Inner>
      </Container>

      {/* 신규 항목 드릴다운 모달 (월별 모드에서 카드 클릭 시) */}
      {newItemModal && (
        <ModalOverlay onClick={() => setNewItemModal(null)}>
          <ModalBox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {month}{' '}
                {newItemModal === 'contractors'
                  ? `신규 시공사 (${newContractorList.length})`
                  : newItemModal === 'sites'
                  ? `신규 현장 (${newSiteList.length})`
                  : newItemModal === 'partners'
                  ? `신규 협력사 (${newPartnerList.length})`
                  : `운영 현장 (${operatingSiteList.length})`}
              </ModalTitle>
              <CloseButton type="button" onClick={() => setNewItemModal(null)}>
                ✕
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              {/* 신규 시공사 */}
              {newItemModal === 'contractors' &&
                (newContractorList.length === 0 ? (
                  <EmptyMsg>데이터 없음</EmptyMsg>
                ) : (
                  <ModalList>
                    <ModalRow $head>
                      <MCell style={{flex: 2}}>시공사명</MCell>
                      <MCell>등록일</MCell>
                    </ModalRow>
                    {newContractorList.map(c => (
                      <ModalRow key={c.uuid}>
                        <MCell style={{flex: 2}}>{c.name || EMPTY_NAME}</MCell>
                        <MCell>{formatDate(c.createdAt)}</MCell>
                      </ModalRow>
                    ))}
                  </ModalList>
                ))}

              {/* 신규 현장 */}
              {newItemModal === 'sites' &&
                (newSiteList.length === 0 ? (
                  <EmptyMsg>데이터 없음</EmptyMsg>
                ) : (
                  <ModalList>
                    <ModalRow $head>
                      <MCell style={{flex: 2}}>현장명</MCell>
                      <MCell>상태</MCell>
                      <MCell style={{flex: 1.5}}>시공사</MCell>
                      <MCell>생성일</MCell>
                    </ModalRow>
                    {newSiteList.map(s => (
                      <ModalRow key={s.uuid}>
                        <MCell style={{flex: 2}}>{s.name || EMPTY_NAME}</MCell>
                        <MCell>
                          <StatusBadge
                            statusKey={getSiteStatusKey(s.state, s.allowState)}
                          />
                        </MCell>
                        <MCell style={{flex: 1.5}}>
                          {s.contractorName || EMPTY_NAME}
                        </MCell>
                        <MCell>{formatDate(s.createdAt)}</MCell>
                      </ModalRow>
                    ))}
                  </ModalList>
                ))}

              {/* 신규 협력사 */}
              {newItemModal === 'partners' &&
                (newPartnerList.length === 0 ? (
                  <EmptyMsg>데이터 없음</EmptyMsg>
                ) : (
                  <ModalList>
                    <ModalRow $head>
                      <MCell style={{flex: 2}}>협력사명</MCell>
                      <MCell style={{flex: 1.5}}>시공사</MCell>
                      <MCell>등록일</MCell>
                    </ModalRow>
                    {newPartnerList.map((p: any) => (
                      <ModalRow key={getPartnerDedupeKey(p)}>
                        <MCell style={{flex: 2}}>{p?.name || EMPTY_NAME}</MCell>
                        <MCell style={{flex: 1.5}}>
                          {p?.contractor?.name || EMPTY_NAME}
                        </MCell>
                        <MCell>{formatDate(p?.createdAt)}</MCell>
                      </ModalRow>
                    ))}
                  </ModalList>
                ))}

              {/* 해당 월 운영 현장 (운영기간 overlap 기준) */}
              {newItemModal === 'operating' &&
                (operatingSiteList.length === 0 ? (
                  <EmptyMsg>데이터 없음</EmptyMsg>
                ) : (
                  <ModalList>
                    <ModalRow $head>
                      <MCell style={{flex: 2}}>현장명</MCell>
                      <MCell style={{flex: 1.5}}>시공사</MCell>
                      <MCell style={{flex: 1.6}}>운영기간</MCell>
                      <MCell>현재상태</MCell>
                    </ModalRow>
                    {operatingSiteList.map(s => (
                      <ModalRow key={s.uuid}>
                        <MCell style={{flex: 2}}>{s.name || EMPTY_NAME}</MCell>
                        <MCell style={{flex: 1.5}}>
                          {s.contractorName || EMPTY_NAME}
                        </MCell>
                        <MCell style={{flex: 1.6}}>
                          {formatDate(s.constructionStartDate)} ~{' '}
                          {formatDate(s.constructionEndDate)}
                        </MCell>
                        <MCell>
                          <StatusBadge
                            statusKey={getSiteStatusKey(s.state, s.allowState)}
                          />
                        </MCell>
                      </ModalRow>
                    ))}
                  </ModalList>
                ))}
            </ModalBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </ScreenLayout>
  );
};

export default ContractorStatistics;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${minmax(14)};
  margin-bottom: ${minmax(30)};
`;

const Card = styled.div<{$clickable?: number}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${minmax(18)} ${minmax(10)};
  background-color: ${colors.whiteSmoke};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(8)};
  ${props =>
    props.$clickable &&
    css`
      cursor: pointer;
      transition: box-shadow 0.15s, border-color 0.15s;
      &:hover {
        border-color: ${colors.primary2};
        box-shadow: 0 ${minmax(2)} ${minmax(8)} rgba(49, 87, 122, 0.18);
      }
    `}
`;

const CardValue = styled.span`
  font-size: ${minmax(30)};
  font-weight: 700;
  color: ${colors.black};
`;

const CardLabel = styled.span`
  margin-top: ${minmax(6)};
  font-size: ${minmax(16)};
  color: ${colors.graniteGray};
  text-align: center;
`;

const CardHint = styled.span`
  color: ${colors.primary2};
  font-size: ${minmax(14)};
  white-space: nowrap;
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${minmax(20)};
  flex-wrap: wrap;
  margin-bottom: ${minmax(10)};
`;

const PeriodWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${minmax(10)};
`;

const PeriodLabel = styled.span`
  font-size: ${minmax(18)};
  font-weight: 600;
  color: ${colors.black};
  margin-right: ${minmax(4)};
`;

const PeriodButton = styled.button<{$active: number}>`
  padding: ${minmax(8)} ${minmax(18)};
  border-radius: ${minmax(6)};
  border: 1px solid
    ${props => (props.$active ? colors.primary2 : colors.lightGrey)};
  background-color: ${props =>
    props.$active ? colors.primary2 : colors.white};
  color: ${props => (props.$active ? colors.white : colors.darkGray)};
  font-size: ${minmax(16)};
  cursor: pointer;
`;

const MonthInput = styled.input`
  height: ${minmax(40)};
  padding: 0 ${minmax(10)};
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(6)};
  font-size: ${minmax(16)};
  color: ${colors.black};
`;

const ResultMeta = styled.div`
  font-size: ${minmax(16)};
  color: ${colors.graniteGray};
  margin-bottom: ${minmax(14)};
`;

const SearchArea = styled.div`
  width: 40%;
  min-width: ${minmax(260)};
  height: ${minmax(56)};
`;

const InputWrap = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ResultArea = styled.div`
  margin-bottom: ${minmax(40)};
  border-bottom: 1px solid ${colors.darkGray};
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${minmax(52)};
  background-color: #f7f7f7;
`;

const HCell = styled.div`
  flex: 1;
  text-align: center;
  color: #585858;
  font-size: ${minmax(18)};
`;

const ResultItem = styled.div<{$open: number}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: ${minmax(52)};
  padding: ${minmax(8)} 0;
  border-top: 1px solid ${colors.lightGrey};
  cursor: pointer;
  background-color: ${props =>
    props.$open ? colors.whiteSmoke : colors.white};
  &:hover {
    background-color: ${colors.whiteSmoke};
  }
`;

const Cell = styled.div`
  flex: 1;
  text-align: center;
  font-size: ${minmax(18)};
  color: ${colors.black};
`;

const Badge = styled.span`
  display: inline-block;
  padding: ${minmax(4)} ${minmax(12)};
  border-radius: ${minmax(14)};
  color: ${colors.white};
  font-size: ${minmax(15)};
  white-space: nowrap;
`;

const AttendanceTag = styled.span<{$on: number}>`
  display: inline-block;
  padding: ${minmax(4)} ${minmax(10)};
  border-radius: ${minmax(6)};
  font-size: ${minmax(15)};
  white-space: nowrap;
  border: 1px solid
    ${props => (props.$on ? colors.darkLimeGreen : colors.lightGrey)};
  color: ${props => (props.$on ? colors.darkLimeGreen : colors.darkGray)};
  background-color: ${props => (props.$on ? '#eef7f0' : colors.white)};
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${minmax(20)};
  padding: ${minmax(20)} ${minmax(24)};
  background-color: #fafafa;
  border-top: 1px dashed ${colors.lightGrey};
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailTitle = styled.div`
  font-size: ${minmax(18)};
  font-weight: 600;
  color: ${colors.black};
  margin-bottom: ${minmax(10)};
`;

const SubNote = styled.span`
  font-size: ${minmax(14)};
  font-weight: 400;
  color: ${colors.darkGray};
  margin-left: ${minmax(6)};
`;

const ChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${minmax(8)};
`;

const Chip = styled.span`
  display: inline-block;
  padding: ${minmax(4)} ${minmax(12)};
  border-radius: ${minmax(14)};
  background-color: ${colors.lighterGray};
  color: ${colors.black};
  font-size: ${minmax(15)};
`;

const SiteRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${minmax(12)};
  padding: ${minmax(10)} 0;
  border-bottom: 1px solid ${colors.lighterGray};
  &:last-child {
    border-bottom: none;
  }
`;

const SiteName = styled.span`
  min-width: ${minmax(200)};
  font-size: ${minmax(17)};
  color: ${colors.black};
`;

const SitePartners = styled.div`
  flex: 1;
  min-width: ${minmax(200)};
`;

const SiteMeta = styled.div`
  flex-basis: 100%;
  font-size: ${minmax(14)};
  color: ${colors.darkGray};
`;

const MutedText = styled.span`
  font-size: ${minmax(15)};
  color: ${colors.darkGray};
`;

const EmptyMsg = styled.div`
  text-align: center;
  color: ${colors.darkGray};
  font-size: ${minmax(18)};
  margin: ${minmax(20)} 0;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  width: min(${minmax(820)}, 90vw);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: ${minmax(10)};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${minmax(16)} ${minmax(20)};
  border-bottom: 1px solid ${colors.lightGrey};
`;

const ModalTitle = styled.span`
  font-size: ${minmax(20)};
  font-weight: 700;
  color: ${colors.black};
`;

const CloseButton = styled.button`
  font-size: ${minmax(20)};
  color: ${colors.darkGray};
  cursor: pointer;
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: ${minmax(12)} ${minmax(20)} ${minmax(20)};
  overflow-y: auto;
`;

const ModalList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalRow = styled.div<{$head?: boolean}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${minmax(10)} 0;
  border-bottom: 1px solid ${colors.lighterGray};
  background-color: ${props =>
    props.$head ? colors.whiteSmoke : 'transparent'};
  font-weight: ${props => (props.$head ? 600 : 400)};
`;

const MCell = styled.div`
  flex: 1;
  text-align: center;
  font-size: ${minmax(16)};
  color: ${colors.black};
`;
